import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { LuTrash2 } from "react-icons/lu";
import { API, colors } from "../shared/constants";
import { styles, inputStyle, labelStyle, fieldStyle } from "../shared/styles";
import { SimpleModal } from "../shared/SimpleModal";

const fmt2 = (n) =>
  n != null && n !== ""
    ? Number(n).toLocaleString("en-AU", { minimumFractionDigits: 2, maximumFractionDigits: 2 })
    : "";

const ROW_GRID = "160px 1fr 140px 110px 130px 64px 40px";

// ── Single price item row (edit existing) ──────────────────────────────────
function PriceItemRow({ item, assetTypeName, suppliers = [], onSaved, onDeleted }) {
  const [name, setName] = useState(item.name || "");
  const [supplierId, setSupplierId] = useState(item.supplier ? String(item.supplier) : "");
  const [cost, setCost] = useState(item.cost_ex_gst != null ? String(item.cost_ex_gst) : "");
  const [price, setPrice] = useState(String(item.price_ex_gst ?? ""));
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  const dirty =
    name !== (item.name || "") ||
    supplierId !== (item.supplier ? String(item.supplier) : "") ||
    cost !== (item.cost_ex_gst != null ? String(item.cost_ex_gst) : "") ||
    price !== String(item.price_ex_gst ?? "");

  const handleSave = async () => {
    if (!name.trim() || !price || isNaN(parseFloat(price))) return;
    setSaving(true);
    try {
      await axios.patch(`${API}/service-prices/${item.id}/`, {
        name: name.trim(), price_ex_gst: parseFloat(price),
        cost_ex_gst: cost ? parseFloat(cost) : null,
        supplier: supplierId ? parseInt(supplierId) : null,
        unit: item.unit || "ea", category: item.category,
        asset_type: item.asset_type, is_active: true,
      });
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
      onSaved();
    } finally { setSaving(false); }
  };

  const handleDelete = async () => {
    if (!window.confirm(`Delete "${item.name}"?`)) return;
    await axios.delete(`${API}/service-prices/${item.id}/`);
    onDeleted();
  };

  return (
    <div style={{
      display: "grid", gridTemplateColumns: ROW_GRID,
      padding: "7px 20px 7px 20px", borderBottom: `1px solid ${colors.border}`,
      alignItems: "center", gap: "8px", backgroundColor: colors.white,
    }}>
      <div style={{ fontSize: "12px", fontWeight: 600, color: "#1e3a5f" }}>{assetTypeName}</div>
      <input
        style={{ ...inputStyle, padding: "5px 8px", fontSize: "13px" }}
        value={name} onChange={(e) => setName(e.target.value)}
        onKeyDown={(e) => e.key === "Enter" && handleSave()}
      />
      <select
        style={{ ...inputStyle, padding: "5px 8px", fontSize: "13px" }}
        value={supplierId} onChange={(e) => setSupplierId(e.target.value)}
      >
        <option value="">— None —</option>
        {suppliers.map((s) => <option key={s.id} value={String(s.id)}>{s.name}</option>)}
      </select>
      <div style={{ display: "flex", alignItems: "center", gap: "4px" }}>
        <span style={{ color: colors.slate, fontSize: "13px" }}>$</span>
        <input
          type="number" min="0" step="0.01"
          style={{ ...inputStyle, padding: "5px 8px", fontSize: "13px", textAlign: "right", flex: 1 }}
          value={cost} onChange={(e) => setCost(e.target.value)}
          placeholder="0.00"
        />
      </div>
      <div style={{ display: "flex", alignItems: "center", gap: "4px" }}>
        <span style={{ color: colors.slate, fontSize: "13px" }}>$</span>
        <input
          type="number" min="0" step="0.01"
          style={{ ...inputStyle, padding: "5px 8px", fontSize: "13px", textAlign: "right", flex: 1 }}
          value={price} onChange={(e) => setPrice(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && handleSave()}
          placeholder="0.00"
        />
      </div>
      <div style={{ display: "flex", justifyContent: "center" }}>
        {saved ? (
          <span style={{ fontSize: "12px", color: "#16a34a", fontWeight: 600 }}>Saved ✓</span>
        ) : (
          <button onClick={handleSave} disabled={saving || !dirty || !name || !price} style={{
            backgroundColor: dirty && name && price ? "#1e3a5f" : colors.border,
            color: dirty && name && price ? colors.white : colors.slate,
            border: "none", padding: "4px 12px", borderRadius: "10px",
            fontSize: "12px", fontWeight: 600,
            cursor: dirty && name && price ? "pointer" : "default",
          }}>{saving ? "…" : "Save"}</button>
        )}
      </div>
      <div style={{ display: "flex", justifyContent: "center" }}>
        <button onClick={handleDelete} title="Delete" style={{
          background: "none", border: "none", cursor: "pointer",
          color: "#dc2626", padding: "4px", borderRadius: "4px",
          display: "flex", alignItems: "center",
        }}><LuTrash2 size={15} /></button>
      </div>
    </div>
  );
}

// ── Blank row to add a new price item for an asset type ────────────────────
function AddItemRow({ assetType, assetTypeName, suppliers = [], onSaved }) {
  const [name, setName] = useState("");
  const [supplierId, setSupplierId] = useState("");
  const [cost, setCost] = useState("");
  const [price, setPrice] = useState("");
  const [saving, setSaving] = useState(false);

  const handleAdd = async () => {
    if (!name.trim() || !price || isNaN(parseFloat(price))) return;
    setSaving(true);
    try {
      await axios.post(`${API}/service-prices/`, {
        name: name.trim(), price_ex_gst: parseFloat(price),
        cost_ex_gst: cost ? parseFloat(cost) : null,
        supplier: supplierId ? parseInt(supplierId) : null,
        unit: "ea", category: assetType.category,
        asset_type: assetType.id, is_active: true,
      });
      setName(""); setSupplierId(""); setCost(""); setPrice("");
      onSaved();
    } finally { setSaving(false); }
  };

  return (
    <div style={{
      display: "grid", gridTemplateColumns: ROW_GRID,
      padding: "7px 20px 7px 20px", borderBottom: `1px solid ${colors.border}`,
      alignItems: "center", gap: "8px", backgroundColor: "#f8fafc",
    }}>
      <div style={{ fontSize: "12px", fontWeight: 600, color: "#1e3a5f" }}>{assetTypeName}</div>
      <input
        style={{ ...inputStyle, padding: "5px 8px", fontSize: "13px" }}
        value={name} onChange={(e) => setName(e.target.value)}
        placeholder="New invoice item…"
        onKeyDown={(e) => e.key === "Enter" && handleAdd()}
      />
      <select
        style={{ ...inputStyle, padding: "5px 8px", fontSize: "13px" }}
        value={supplierId} onChange={(e) => setSupplierId(e.target.value)}
      >
        <option value="">— None —</option>
        {suppliers.map((s) => <option key={s.id} value={String(s.id)}>{s.name}</option>)}
      </select>
      <div style={{ display: "flex", alignItems: "center", gap: "4px" }}>
        <span style={{ color: colors.slate, fontSize: "13px" }}>$</span>
        <input
          type="number" min="0" step="0.01"
          style={{ ...inputStyle, padding: "5px 8px", fontSize: "13px", textAlign: "right", flex: 1 }}
          value={cost} onChange={(e) => setCost(e.target.value)}
          placeholder="0.00"
        />
      </div>
      <div style={{ display: "flex", alignItems: "center", gap: "4px" }}>
        <span style={{ color: colors.slate, fontSize: "13px" }}>$</span>
        <input
          type="number" min="0" step="0.01"
          style={{ ...inputStyle, padding: "5px 8px", fontSize: "13px", textAlign: "right", flex: 1 }}
          value={price} onChange={(e) => setPrice(e.target.value)}
          placeholder="0.00"
          onKeyDown={(e) => e.key === "Enter" && handleAdd()}
        />
      </div>
      <div style={{ display: "flex", justifyContent: "center" }}>
        <button onClick={handleAdd} disabled={saving || !name || !price} style={{
          backgroundColor: name && price ? "#059669" : colors.border,
          color: name && price ? colors.white : colors.slate,
          border: "none", padding: "4px 10px", borderRadius: "10px",
          fontSize: "12px", fontWeight: 600,
          cursor: name && price ? "pointer" : "default",
        }}>{saving ? "…" : "+ Add"}</button>
      </div>
      <div />
    </div>
  );
}

// ── Asset type section: existing items + add row (no separate sub-header) ──
function AssetTypeRow({ assetType, servicePrices, suppliers, onSaved }) {
  return (
    <>
      {servicePrices.map((sp, i) => (
        <PriceItemRow
          key={sp.id}
          item={sp}
          assetTypeName={i === 0 ? assetType.name : ""}
          suppliers={suppliers}
          onSaved={onSaved}
          onDeleted={onSaved}
        />
      ))}
      <AddItemRow
        assetType={assetType}
        assetTypeName={servicePrices.length === 0 ? assetType.name : ""}
        suppliers={suppliers}
        onSaved={onSaved}
      />
    </>
  );
}

// ── Main page ──────────────────────────────────────────────────────────────
export function ServicePricesSettings() {
  const [servicePrices, setServicePrices] = useState([]);
  const [assetTypes, setAssetTypes] = useState([]);
  const [categories, setCategories] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [collapsedCats, setCollapsedCats] = useState(new Set());

  // Custom item modal state
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [unit, setUnit] = useState("ea");
  const [price, setPrice] = useState("");
  const [category, setCategory] = useState("");
  const [isActive, setIsActive] = useState(true);

  const toggleCat = (catName) =>
    setCollapsedCats((prev) => {
      const next = new Set(prev);
      next.has(catName) ? next.delete(catName) : next.add(catName);
      return next;
    });

  const loadAll = useCallback(() => {
    Promise.all([
      axios.get(`${API}/service-prices/`),
      axios.get(`${API}/asset-types/`),
      axios.get(`${API}/asset-categories/`),
      axios.get(`${API}/suppliers/`),
    ]).then(([priceRes, typeRes, catRes, suppRes]) => {
      setServicePrices(priceRes.data || []);
      setAssetTypes(typeRes.data || []);
      setCategories(catRes.data || []);
      setSuppliers(suppRes.data || []);
    }).finally(() => setLoading(false));
  }, []);

  useEffect(() => { loadAll(); }, [loadAll]);

  // Build a lookup: asset_type_id → [sp, sp, ...]
  const pricesByAssetType = {};
  servicePrices.forEach((sp) => {
    if (sp.asset_type) {
      if (!pricesByAssetType[sp.asset_type]) pricesByAssetType[sp.asset_type] = [];
      pricesByAssetType[sp.asset_type].push(sp);
    }
  });

  // Custom items (no asset_type linked)
  const customItems = servicePrices.filter((sp) => !sp.asset_type);

  // Group asset types by category
  const grouped = {};
  assetTypes.forEach((at) => {
    const cat = categories.find((c) => c.id === at.category);
    const catName = cat?.name || "Other";
    if (!grouped[catName]) grouped[catName] = [];
    grouped[catName].push(at);
  });
  const sortedCategories = Object.keys(grouped).sort();

  const openAdd = () => {
    setEditing(null);
    setName(""); setDescription(""); setUnit("ea"); setPrice(""); setCategory(""); setIsActive(true);
    setError("");
    setShowModal(true);
  };

  const openEdit = (item) => {
    setEditing(item);
    setName(item.name); setDescription(item.description || ""); setUnit(item.unit || "ea");
    setPrice(String(item.price_ex_gst)); setCategory(item.category ? String(item.category) : "");
    setIsActive(item.is_active);
    setError("");
    setShowModal(true);
  };

  const handleModalSave = async () => {
    if (!name.trim()) { setError("Service name is required."); return; }
    if (!price || isNaN(parseFloat(price))) { setError("A valid price is required."); return; }
    setSaving(true); setError("");
    const payload = {
      name: name.trim(), description: description.trim(),
      unit: unit.trim() || "ea", price_ex_gst: parseFloat(price),
      category: category || null, asset_type: null, is_active: isActive,
    };
    try {
      if (editing) {
        await axios.patch(`${API}/service-prices/${editing.id}/`, payload);
      } else {
        await axios.post(`${API}/service-prices/`, payload);
      }
      setShowModal(false);
      loadAll();
    } catch (e) {
      setError("Something went wrong. Please try again.");
    }
    setSaving(false);
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100%" }}>
      <div style={styles.topbar}>
        <div style={styles.pageTitle}>Service Price List</div>
        <button style={styles.addButton} onClick={openAdd}>+ Custom Item</button>
      </div>

      <div style={styles.content}>
        {loading ? (
          <div style={styles.loading}>Loading…</div>
        ) : (
          <>
            {/* Asset-type-linked prices grouped by category */}
            {sortedCategories.map((catName) => {
              const isCollapsed = collapsedCats.has(catName);
              return (
                <div key={catName} style={{ ...styles.card, marginBottom: "16px" }}>
                  {/* Category header — clickable to collapse */}
                  <div
                    onClick={() => toggleCat(catName)}
                    style={{
                      padding: "10px 20px",
                      backgroundColor: "#1e3a5f",
                      color: colors.white,
                      fontSize: "12px",
                      fontWeight: 700,
                      textTransform: "uppercase",
                      letterSpacing: "0.5px",
                      cursor: "pointer",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      userSelect: "none",
                    }}
                  >
                    {catName}
                    <span style={{ fontSize: "12px", opacity: 0.8 }}>{isCollapsed ? "▶" : "▼"}</span>
                  </div>

                  {!isCollapsed && (
                    <>
                      {/* Column headers */}
                      <div style={{
                        display: "grid",
                        gridTemplateColumns: ROW_GRID,
                        padding: "7px 20px 7px 20px",
                        backgroundColor: "#f8fafc",
                        borderBottom: `1px solid ${colors.border}`,
                        fontSize: "11px",
                        fontWeight: 700,
                        color: colors.slate,
                        textTransform: "uppercase",
                        letterSpacing: "0.4px",
                        gap: "8px",
                      }}>
                        <div>Asset Type</div>
                        <div>Invoice Item</div>
                        <div>Supplier</div>
                        <div style={{ textAlign: "right" }}>Cost (ex. GST)</div>
                        <div style={{ textAlign: "right" }}>Price (ex. GST)</div>
                        <div></div>
                        <div></div>
                      </div>

                      {grouped[catName].map((at) => (
                        <AssetTypeRow
                          key={at.id}
                          assetType={at}
                          servicePrices={pricesByAssetType[at.id] || []}
                          suppliers={suppliers}
                          onSaved={loadAll}
                        />
                      ))}
                    </>
                  )}
                </div>
              );
            })}

            {/* Custom items (no asset type link) */}
            {customItems.length > 0 && (
              <div style={styles.card}>
                <div style={{
                  padding: "10px 20px",
                  backgroundColor: "#475569",
                  color: colors.white,
                  fontSize: "12px",
                  fontWeight: 700,
                  textTransform: "uppercase",
                  letterSpacing: "0.5px",
                }}>
                  Custom Items
                </div>
                <div style={{
                  ...styles.tableHeader,
                  gridTemplateColumns: "3fr 1fr 1fr 80px",
                }}>
                  <div>Service Name</div>
                  <div>Unit</div>
                  <div>Price (ex. GST)</div>
                  <div></div>
                </div>
                {customItems.map((item) => (
                  <div
                    key={item.id}
                    style={{
                      display: "grid",
                      gridTemplateColumns: "3fr 1fr 1fr 80px",
                      padding: "12px 20px",
                      borderBottom: `1px solid ${colors.border}`,
                      alignItems: "center",
                      fontSize: "14px",
                      color: colors.text,
                      backgroundColor: colors.white,
                    }}
                  >
                    <div>
                      <div style={{ fontWeight: 500 }}>{item.name}</div>
                      {item.description && <div style={{ fontSize: 12, color: colors.slate, marginTop: 2 }}>{item.description}</div>}
                    </div>
                    <div style={{ color: colors.slate, fontSize: 13 }}>{item.unit || "ea"}</div>
                    <div style={{ fontWeight: 600, color: "#1e3a5f" }}>${fmt2(item.price_ex_gst)}</div>
                    <div style={{ display: "flex", justifyContent: "flex-end" }}>
                      <button
                        onClick={() => openEdit(item)}
                        style={{
                          backgroundColor: colors.blue,
                          color: colors.white,
                          border: "none",
                          padding: "4px 14px",
                          borderRadius: "12px",
                          fontSize: 12,
                          fontWeight: 600,
                          cursor: "pointer",
                        }}
                      >
                        Edit
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {assetTypes.length === 0 && (
              <div style={{ padding: "48px 24px", textAlign: "center", color: colors.slate, fontSize: "14px" }}>
                No asset types found. Add asset types under your site EFSMs first.
              </div>
            )}
          </>
        )}
      </div>

      {showModal && (
        <SimpleModal
          title={editing ? `Edit: ${editing.name}` : "New Custom Service Item"}
          onClose={() => setShowModal(false)}
          onSave={handleModalSave}
          saving={saving}
          error={error}
          wide
        >
          <div style={fieldStyle}>
            <label style={labelStyle}>Service Name *</label>
            <input style={inputStyle} value={name} onChange={(e) => setName(e.target.value)} autoFocus />
          </div>
          <div style={fieldStyle}>
            <label style={labelStyle}>Description (optional)</label>
            <textarea
              style={{ ...inputStyle, height: 64, resize: "vertical" }}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            />
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginBottom: 16 }}>
            <div>
              <label style={labelStyle}>Unit</label>
              <input style={inputStyle} value={unit} onChange={(e) => setUnit(e.target.value)} placeholder="ea" />
            </div>
            <div>
              <label style={labelStyle}>Price (ex. GST) *</label>
              <input
                style={inputStyle} type="number" min="0" step="0.01"
                value={price} onChange={(e) => setPrice(e.target.value)} placeholder="0.00"
              />
            </div>
          </div>
          <div style={fieldStyle}>
            <label style={labelStyle}>Category (optional)</label>
            <select style={inputStyle} value={category} onChange={(e) => setCategory(e.target.value)}>
              <option value="">— No category —</option>
              {categories.map((c) => <option key={c.id} value={String(c.id)}>{c.name}</option>)}
            </select>
          </div>
          <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 14, cursor: "pointer" }}>
            <input type="checkbox" checked={isActive} onChange={(e) => setIsActive(e.target.checked)} />
            Active
          </label>
        </SimpleModal>
      )}
    </div>
  );
}
